import { useEffect, useRef } from 'react';
import type { Gender, MaritalStatus, PatientLookup } from '#shared/types';
import { useDebounced } from '#client/hooks/useDebounced';
import { useKeyedRequest } from '#client/hooks/useKeyedRequest';
import { api } from '#client/lib/api';

import { ENDPOINT } from '#client/lib/endpoints';
import { MIN_PHONE_DIGITS } from '#shared/types';

export type PatientMatch = NonNullable<PatientLookup['match']>;

/**
 * Whether the phone number being typed belongs to somebody the lab has seen.
 *
 * `onMatch` runs once per person found — from the response, not from an effect
 * copying it into state — so the form can prefill from their last visit without
 * a cascading render. Pass `enabled: false` when editing: an existing visit
 * already belongs to someone.
 */
export function usePatientLookup(
  phone: string,
  enabled: boolean,
  onMatch: (match: PatientMatch) => void,
): PatientMatch | null {
  const settled = useDebounced(phone.trim(), 400);
  const onMatchRef = useRef(onMatch);
  useEffect(() => {
    onMatchRef.current = onMatch;
  });
  const announced = useRef<string | null>(null);

  // Asking with fewer digits than `canonicalPhone` matches on only fetches
  // "nobody" on every keystroke.
  const key =
    enabled && settled.replace(/\D/g, '').length >= MIN_PHONE_DIGITS ? settled : undefined;

  const { data } = useKeyedRequest<PatientLookup>(
    key,
    async (signal) => {
      const result = await api.get<PatientLookup>(
        ENDPOINT.patientLookup,
        { phone: settled },
        signal,
      );
      if (result.match && !signal.aborted && announced.current !== result.match.mr_no) {
        announced.current = result.match.mr_no;
        onMatchRef.current(result.match);
      }
      return result;
    },
    'Could not look up this phone number',
  );

  return key === undefined ? null : (data?.match ?? null);
}

/**
 * The previous visit's details, over whatever the desk has not typed yet.
 *
 * Text is filled only where the field is still empty, so a name typed before
 * the number is not overwritten. Gender and marital status always start from a
 * default, so they take the last visit's value outright.
 */
export function prefillFrom<
  T extends {
    name?: string | undefined;
    gender: Gender;
    married_status: MaritalStatus;
    age_year: number;
    age_month: number;
    age_days: number;
    cnic_number?: string | null | undefined;
    email?: string | null | undefined;
  },
>(current: T, last: PatientMatch['last_visit']): T {
  const blank = (value: string | null | undefined) => !value?.trim();
  return {
    ...current,
    ...('name' in current && blank(current.name) ? { name: last.name } : {}),
    gender: last.gender,
    married_status: last.married_status,
    ...(current.age_year === 0 && current.age_month === 0 && current.age_days === 0
      ? { age_year: last.age_year, age_month: last.age_month, age_days: last.age_days }
      : {}),
    ...(blank(current.cnic_number) && last.cnic_number ? { cnic_number: last.cnic_number } : {}),
    ...(blank(current.email) && last.email ? { email: last.email } : {}),
  };
}
